// @ts-check

import {
  createIslandLayer,
  createIslandLabelLayer,
  createCulturalRegionLayers,
  createPolygonLabelLayer,
  createMigrationRouteLayer,
  createMigrationWaveRouteLayer,
  createMigrationWaveArrowLayer,
  createMigrationWaveLabelLayer,
  createMigrationWavePolygonLayers
} from "./mapboxLayers.js";
import {
  setupIslandClickHandler,
  setupIslandHoverHandlers,
  setupRegionClickHandler,
  setupRegionHoverHandlers,
  setupRouteClickHandler
} from "./mapboxEvents.js";
import {
  filterIslandFeatures,
  filterRegionFeatures,
  enrichFeaturesWithCsvData,
  createMigrationRoutesGeoJson,
  matchIslandWithCsvData,
  matchMigrationWavePolygonWithCsvData
} from "./geoJsonProcessing.js";
import {
  createArrowheadFeatures,
  createRouteLabelFeatures,
  filterRoutesByLayerId,
  filterPolygonsByLayerId
} from "./mapboxArrows.js";
import { findFirstLabelLayer } from "./mapboxLabelManager.js";

/**
 * Mapbox source and layer management.
 * Handles adding, removing, and updating map sources and layers.
 */

/**
 * Remove existing layer(s) and source from map.
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {string|string[]} layerIds - Layer ID(s) to remove
 * @param {string} sourceId - Source ID to remove
 */
export function removeExistingLayer(map, layerIds, sourceId) {
  if (!map.getSource(sourceId)) return;

  const layers = Array.isArray(layerIds) ? layerIds : [layerIds];
  layers.forEach((layerId) => {
    if (map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }
  });

  map.removeSource(sourceId);
}

/**
 * Set visibility for a single layer.
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {string} layerId - Layer ID to update
 * @param {boolean|"visible" | "none"} visibility - Visibility state (boolean or "visible"/"none")
 */
export function setLayerVisibility(map, layerId, visibility) {
  if (!map.getLayer(layerId)) return;

  const visibilityValue =
    typeof visibility === "boolean"
      ? visibility
        ? "visible"
        : "none"
      : visibility;

  map.setLayoutProperty(layerId, "visibility", visibilityValue);
}

/**
 * Set visibility for multiple layers at once.
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {string[]} layerIds - Array of layer IDs to update
 * @param {boolean} visible - Whether layers should be visible
 */
export function setMultipleLayerVisibility(map, layerIds, visible) {
  const visibility = visible ? "visible" : "none";
  layerIds.forEach((layerId) => {
    setLayerVisibility(map, layerId, visibility);
  });
}

/**
 * Add island marker layer to map with all interactions.
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {Object} mapGeoJson - Full GeoJSON data
 * @param {Array} islands - Island CSV data
 * @param {string} islandIdField - CSV field for island ID
 * @param {Function} onIslandSelect - Callback when island is selected
 * @returns {Object} Object with cleanup function
 */
export function addIslandMarkers(
  map,
  mapGeoJson,
  islands,
  islandIdField,
  onIslandSelect
) {
  removeExistingLayer(map, ["islands-layer", "islands-labels"], "islands");

  const geoJsonData = filterIslandFeatures(mapGeoJson);

  map.addSource("islands", {
    type: "geojson",
    data: geoJsonData
  });

  // Find if any migration wave label layers exist to insert islands under them
  // This ensures migration wave labels (which should be topmost) remain visible
  const layers = map.getStyle().layers;
  const migrationLabelLayer = layers.find(
    (layer) =>
      layer.id.includes("migration-wave") && layer.id.includes("labels")
  );
  const beforeId = migrationLabelLayer ? migrationLabelLayer.id : undefined;

  // Add circle markers first
  map.addLayer(createIslandLayer("islands"), beforeId);

  // Add native Mapbox labels layer (will use same source)
  map.addLayer(createIslandLabelLayer("islands"), beforeId);

  // Setup click handlers for both circle markers and labels
  const handleIslandClick = (feature) => {
    const islandData = matchIslandWithCsvData(feature, islands, islandIdField);
    onIslandSelect(islandData);
  };

  setupIslandClickHandler(map, "islands-layer", handleIslandClick);
  setupIslandClickHandler(map, "islands-labels", handleIslandClick);

  // Setup hover handlers for cursor change only
  const cleanup = setupIslandHoverHandlers(map, "islands-layer");
  setupIslandHoverHandlers(map, "islands-labels");

  return cleanup;
}

/**
 * Add cultural region polygon layers to map with all interactions.
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {Object} mapGeoJson - Full GeoJSON data
 * @param {Array} culturalRegions - Cultural region CSV data
 * @param {string} regionIdField - CSV field for region ID
 * @param {Function} onRegionSelect - Callback when region is selected
 */
export function addCulturalRegions(
  map,
  mapGeoJson,
  culturalRegions,
  regionIdField,
  onRegionSelect
) {
  removeExistingLayer(
    map,
    [
      "cultural-regions-fill",
      "cultural-regions-outline",
      "cultural-regions-labels"
    ],
    "cultural-regions"
  );

  const regionGeoJson = filterRegionFeatures(mapGeoJson);
  const enrichedGeoJson = enrichFeaturesWithCsvData(
    regionGeoJson,
    culturalRegions,
    regionIdField,
    "region"
  );

  map.addSource("cultural-regions", {
    type: "geojson",
    data: enrichedGeoJson
  });

  // Find first label layer to insert our layers beneath it
  const firstLabelLayerId = findFirstLabelLayer(map);
  console.log(
    "firstLabelLayerId for cultural region insertion",
    firstLabelLayerId
  );
  const { fill, outline } = createCulturalRegionLayers("cultural-regions");
  map.addLayer(fill, firstLabelLayerId);
  map.addLayer(outline, firstLabelLayerId);

  // Add labels layer (will be centered within polygons automatically, one per polygon)
  map.addLayer(
    createPolygonLabelLayer("cultural-regions", "cultural-regions-labels", [
      "coalesce",
      ["get", "place_name"],
      ["get", "Region Name"],
      ""
    ]),
    firstLabelLayerId
  );

  setupRegionClickHandler(map, "cultural-regions-fill", onRegionSelect);
  setupRegionHoverHandlers(map, "cultural-regions-fill");
}

/**
 * Add migration route line layers to map with all interactions.
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {Array} migrationRoutes - Migration route data
 * @param {Function} onRouteSelect - Callback when route is selected
 */
export function addMigrationRoutes(map, migrationRoutes, onRouteSelect) {
  if (!migrationRoutes.length) return;

  removeExistingLayer(map, "migration-routes-line", "migration-routes");

  const geoJsonData = createMigrationRoutesGeoJson(migrationRoutes);

  map.addSource("migration-routes", {
    type: "geojson",
    data: geoJsonData
  });

  // Find first label layer to insert our layer beneath it
  const firstLabelLayerId = findFirstLabelLayer(map);
  map.addLayer(
    createMigrationRouteLayer("migration-routes"),
    firstLabelLayerId
  );
  setupRouteClickHandler(map, "migration-routes-line", onRouteSelect);
}

/**
 * Add migration wave layers to map (routes, arrows, labels, polygons).
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {Object} routesGeoJson - Full routes GeoJSON FeatureCollection
 * @param {Object} mapGeoJson - Full map GeoJSON FeatureCollection (for polygons)
 * @param {string} layerId - Layer ID from properties (e.g., "migration_wave_1")
 * @param {string} waveNumber - Wave number for layer IDs (e.g., "1")
 * @param {string} color - Color for routes, arrows, and polygons (default: "#ffa500")
 * @param {Array} migrationRoutes - Migration routes CSV data for enrichment
 * @param {string} routeIdField - CSV field name for route ID (e.g., "Route ID")
 * @param {Function} onPolygonSelect - Callback when polygon is selected
 */
export function addMigrationWaveLayers(
  map,
  routesGeoJson,
  mapGeoJson,
  layerId,
  waveNumber,
  color = "#ffa500",
  migrationRoutes = [],
  routeIdField = "Route ID",
  onPolygonSelect
) {
  // Filter routes by layer_id
  const filteredRoutes = filterRoutesByLayerId(routesGeoJson, layerId);

  // Filter polygons by layer_id
  const filteredPolygons = filterPolygonsByLayerId(mapGeoJson, layerId);

  const sourceId = `migration-wave-${waveNumber}`;
  const routeLayerId = `${sourceId}-routes`;
  const arrowLayerId = `${sourceId}-arrows`;
  const labelLayerId = `${sourceId}-labels`;
  const arrowSourceId = `${sourceId}-arrows-source`;
  const labelSourceId = `${sourceId}-labels-source`;
  const polygonSourceId = `${sourceId}-polygons`;
  const polygonFillLayerId = `${sourceId}-polygon-fill`;
  const polygonOutlineLayerId = `${sourceId}-polygon-outline`;

  // Remove existing layers if they exist
  const layersToRemove = [
    routeLayerId,
    arrowLayerId,
    labelLayerId,
    polygonFillLayerId,
    polygonOutlineLayerId
  ];
  layersToRemove.forEach((layerIdToRemove) => {
    if (map.getLayer(layerIdToRemove)) {
      map.removeLayer(layerIdToRemove);
    }
  });

  // Remove existing sources if they exist
  const sourcesToRemove = [
    sourceId,
    arrowSourceId,
    labelSourceId,
    polygonSourceId
  ];
  sourcesToRemove.forEach((sourceIdToRemove) => {
    if (map.getSource(sourceIdToRemove)) {
      map.removeSource(sourceIdToRemove);
    }
  });

  const firstLabelLayerId = findFirstLabelLayer(map);

  // Add route line source and layer (if routes exist)
  if (filteredRoutes.features.length > 0) {
    map.addSource(sourceId, {
      type: "geojson",
      data: filteredRoutes
    });

    map.addLayer(
      createMigrationWaveRouteLayer(sourceId, routeLayerId, color),
      firstLabelLayerId
    );

    // Create and add arrowhead features
    const arrowFeatures = createArrowheadFeatures(filteredRoutes);
    map.addSource(arrowSourceId, {
      type: "geojson",
      data: arrowFeatures
    });
    map.addLayer(
      createMigrationWaveArrowLayer(arrowSourceId, arrowLayerId, color),
      firstLabelLayerId
    );

    // Create and add label features (midpoints)
    const labelFeatures = createRouteLabelFeatures(filteredRoutes);
    map.addSource(labelSourceId, {
      type: "geojson",
      data: labelFeatures
    });
    map.addLayer(createMigrationWaveLabelLayer(labelSourceId, labelLayerId));
  }

  // Add polygon layers (if polygons exist)
  if (filteredPolygons.features.length > 0) {
    // Enrich polygons with CSV data
    const enrichedPolygons = filteredPolygons.features.map((feature) => {
      const csvRoute = migrationRoutes.find(
        (route) => route[routeIdField] === feature.properties.mapbox_id
      );
      return {
        ...feature,
        properties: {
          ...feature.properties,
          ...(csvRoute || {}),
          type: "route"
        }
      };
    });

    const enrichedPolygonGeoJson = {
      type: "FeatureCollection",
      features: enrichedPolygons
    };

    map.addSource(polygonSourceId, {
      type: "geojson",
      data: /** @type {import('geojson').FeatureCollection} */ (
        enrichedPolygonGeoJson
      )
    });

    const { fill, outline } = createMigrationWavePolygonLayers(
      polygonSourceId,
      polygonFillLayerId,
      polygonOutlineLayerId,
      color
    );

    map.addLayer(fill, firstLabelLayerId);
    map.addLayer(outline, firstLabelLayerId);

    // Setup click handler for polygons
    if (onPolygonSelect) {
      const handlePolygonClick = (event) => {
        const feature = event.features[0];
        const routeData = matchMigrationWavePolygonWithCsvData(
          feature,
          migrationRoutes,
          routeIdField
        );
        console.log("🌊 MIGRATION WAVE POLYGON CLICK DEBUG:", {
          polygonProperties: feature.properties,
          clickedPoint: event.point,
          lngLat: event.lngLat,
          routeName:
            routeData?.Name ||
            routeData?.description ||
            routeData?.["Route ID"] ||
            "Unknown",
          routeType: routeData?.type || "route"
        });
        onPolygonSelect(routeData);
      };

      map.on("click", polygonFillLayerId, handlePolygonClick);
      setupRegionHoverHandlers(map, polygonFillLayerId);
    }
  }
}

/**
 * Update visibility of all map layers based on active state.
 * @param {import('mapbox-gl').Map} map - Mapbox map instance
 * @param {Object} activeLayers - Layer visibility state
 * @param {boolean} activeLayers.cultural - Cultural regions visibility
 * @param {boolean} activeLayers.migration_wave_1 - Migration wave 1 visibility
 * @param {boolean} activeLayers.migration_wave_2 - Migration wave 2 visibility
 */
export function updateLayerVisibility(map, activeLayers) {
  setLayerVisibility(map, "islands-layer", "visible");
  setLayerVisibility(map, "islands-labels", "visible");
  setMultipleLayerVisibility(
    map,
    [
      "cultural-regions-fill",
      "cultural-regions-outline",
      "cultural-regions-labels"
    ],
    activeLayers.cultural
  );

  // Update migration wave 1 visibility (routes, arrows, labels, polygons)
  const wave1Visible = activeLayers.migration_wave_1 || false;
  setMultipleLayerVisibility(
    map,
    [
      "migration-wave-1-routes",
      "migration-wave-1-arrows",
      "migration-wave-1-labels",
      "migration-wave-1-polygon-fill",
      "migration-wave-1-polygon-outline"
    ],
    wave1Visible
  );

  // Update migration wave 2 visibility (routes, arrows, labels, polygons)
  const wave2Visible = activeLayers.migration_wave_2 || false;
  setMultipleLayerVisibility(
    map,
    [
      "migration-wave-2-routes",
      "migration-wave-2-arrows",
      "migration-wave-2-labels",
      "migration-wave-2-polygon-fill",
      "migration-wave-2-polygon-outline"
    ],
    wave2Visible
  );
}
